import React, { useState, useEffect } from 'react';
import { sanitizeRetailerCode, validateRetailerCode } from './authUtils';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { ScrollArea } from './components/ui/scroll-area';
import { Wine, AlertTriangle } from 'lucide-react';

function Login({ onLogin, onSignup, onResetPassword }) {
  const [error, setError] = useState('');
  const [blockedMessage, setBlockedMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  // Check for blocked account message on mount
  useEffect(() => {
    const isBlocked = localStorage.getItem('accountBlocked');
    const message = localStorage.getItem('blockedMessage');
    
    if (isBlocked === 'true' && message) {
      setBlockedMessage(message);
      // Clear the localStorage items
      localStorage.removeItem('accountBlocked');
      localStorage.removeItem('blockedMessage');
    }
  }, []);

  // Use refs for uncontrolled inputs (better for Android Chrome)
  const retailerCodeRef = React.useRef(null);
  const passwordRef = React.useRef(null);
  const lastSubmitTimeRef = React.useRef(0);

  const handleLogin = async () => {
    // Prevent rapid double submissions (debounce for 1 second)
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 1000) {
      return;
    }
    lastSubmitTimeRef.current = now;

    // Clear error first
    setError('');
    setLoading(true);

    // Get values from refs
    const currentRetailerCode = (retailerCodeRef.current?.value || '').trim();
    const currentPassword = (passwordRef.current?.value || '').trim();
    

    // Client-side validation
    const cleanRetailerCode = sanitizeRetailerCode(currentRetailerCode);
    if (!validateRetailerCode(cleanRetailerCode)) {
      setError('Retailer code must be 7 digits or 7 digits followed by (letter), e.g., 2302679 or 2302679(R)');
      setLoading(false);
      return;
    }

    if (!currentPassword || currentPassword.length < 1) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    try {
      // Pass credentials to AuthContext for actual login
      const loginResult = await onLogin({
        retailerCode: cleanRetailerCode,
        password: currentPassword
      });
      
      if (!loginResult.success) {
        // Check if account is blocked
        if (loginResult.blocked) {
          setBlockedMessage(loginResult.error || 'Your account has been suspended');
          setError(''); // Clear regular error
        } else {
          setError(loginResult.error || 'Login failed');
          setBlockedMessage(''); // Clear blocked message
        }
      }
      // If successful, AuthContext handles authentication state
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please try again.');
      setBlockedMessage(''); // Clear blocked message
    }

    setLoading(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) {
      handleLogin();
    }
  };

  // Allow typing both formats: 7 digits or 7 digits + (letter)
  const handleRetailerCodeInput = (e) => {
    let value = e.target.value;
    
    // Remove spaces
    value = value.replace(/\s/g, '');
    
    // Allow only: digits, (, ), and letters A-Z
    value = value.replace(/[^0-9()A-Za-z]/g, '');
    
    // Limit to 10 characters
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    e.target.value = value;
  };

  const handlePolicyClick = (policyKey) => {
    setSelectedPolicy(policyKey);
    setPolicyDialogOpen(true);
  };

  // Policy content components (simplified for dialog)
  const PrivacyPolicyContent = () => (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
      <section>
        <h3 className="font-semibold mb-2">1. Introduction</h3>
        <p className="mb-4">Welcome to Liquor Store Inventory Management System. We are committed to protecting your privacy and ensuring the security of your personal information.</p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">2. Information We Collect</h3>
        <p className="mb-2">We may collect:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Name and contact information</li>
          <li>Business information (shop name, address, etc)</li>
          <li>Payment information (processed securely through Razorpay)</li>
          <li>Account credentials</li>
        </ul>
      </section>
      <section>
        <h3 className="font-semibold mb-2">3. How We Use Your Information</h3>
        <p className="mb-2">We use collected information to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Provide and maintain our service</li>
          <li>Process payments and manage subscriptions</li>
          <li>Send important updates and notifications</li>
          <li>Improve our service and develop new features</li>
        </ul>
      </section>
      <section>
        <h3 className="font-semibold mb-2">4. Contact Us</h3>
        <p>Email: contact@easysheetsdaily.com</p>
      </section>
      <section className="mt-6 pt-4 border-t">
        <p className="text-xs text-muted-foreground text-center">Maintained by NK TECH LABS</p>
      </section>
    </div>
  );

  const TermsOfServiceContent = () => (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
      <section>
        <h3 className="font-semibold mb-2">1. Acceptance of Terms</h3>
        <p className="mb-4">By accessing and using our Service, you accept and agree to be bound by these terms.</p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">2. Description of Service</h3>
        <p className="mb-2">Our Service provides inventory management solutions including:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Stock management and tracking</li>
          <li>Invoice processing and management</li>
          <li>Sales reporting and analytics</li>
          <li>Payment tracking</li>
        </ul>
      </section>
      <section>
        <h3 className="font-semibold mb-2">3. Subscription and Payment</h3>
        <p className="mb-4">We offer various subscription plans. By subscribing, you agree to pay the subscription fees as specified. Payments are processed securely through Razorpay.</p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">4. Contact Us</h3>
        <p>Email: contact@easysheetsdaily.com</p>
      </section>
      <section className="mt-6 pt-4 border-t">
        <p className="text-xs text-muted-foreground text-center">Maintained by NK TECH LABS</p>
      </section>
    </div>
  );

  const RefundPolicyContent = () => (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
      <section>
        <h3 className="font-semibold mb-2">1. Refund Policy</h3>
        <p className="mb-4">
          All subscription payments are <strong>non-refundable</strong>. Once a payment has been processed, we do not provide refunds for any reason, including but not limited to:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Cancellation of subscription</li>
          <li>Non-usage of the service</li>
          <li>Dissatisfaction with the service</li>
        </ul>
      </section>
      <section>
        <h3 className="font-semibold mb-2">2. Subscription Cancellation</h3>
        <p className="mb-4">
          While subscriptions are non-refundable, you may cancel your subscription at any time. Cancellation will prevent future charges, but will not result in a refund for the current billing period. You will retain access to the service until the end of your paid period.
        </p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">3. Special Circumstances</h3>
        <p className="mb-4">
          In exceptional circumstances such as duplicate charges or billing errors, please contact us immediately. We will investigate and resolve such issues on a case-by-case basis.
        </p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">4. Contact Us</h3>
        <p>For any questions regarding this policy, please contact us at:</p>
        <p className="mt-2"><strong>Email:</strong> contact@easysheetsdaily.com</p>
      </section>
      <section className="mt-6 pt-4 border-t">
        <p className="text-xs text-muted-foreground text-center">Maintained by NK TECH LABS</p>
      </section>
    </div>
  );

  const CancellationPolicyContent = () => (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
      <section>
        <h3 className="font-semibold mb-2">1. How to Cancel</h3>
        <p className="mb-2">You can cancel your subscription by:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Logging in and going to Subscription page</li>
          <li>Sending a cancellation request on the same page</li>
        </ul>
      </section>
      <section>
        <h3 className="font-semibold mb-2">2. Cancellation Timing</h3>
        <p className="mb-4">Cancellation takes effect immediately. You will retain access until the end of your current billing period. No further charges will be made.</p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">3. Contact Us</h3>
        <p>Email: contact@easysheetsdaily.com</p>
      </section>
      <section className="mt-6 pt-4 border-t">
        <p className="text-xs text-muted-foreground text-center">Maintained by NK TECH LABS</p>
      </section>
    </div>
  );

  // Policy content data
  const policies = {
    privacy: {
      title: 'Privacy Policy',
      content: PrivacyPolicyContent
    },
    terms: {
      title: 'Terms of Service',
      content: TermsOfServiceContent
    },
    refund: {
      title: 'Refund Policy',
      content: RefundPolicyContent
    },
    cancellation: {
      title: 'Cancellation Policy',
      content: CancellationPolicyContent
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <Wine className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">Easy Sheets, Daily</CardTitle>
          <CardDescription>
            Sign in to your liquor store inventory
          </CardDescription>
        </CardHeader>
        <CardContent>
          {blockedMessage && (
            <Alert variant="destructive" className="mb-4 border-2 border-red-600">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                <div className="font-semibold text-base">Account Blocked</div>
                <div className="mt-2 whitespace-pre-line">{blockedMessage}</div>
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="retailer-code" className="text-left block">
                  Retailer Code
                </Label>
                <Input
                  id="retailer-code"
                  ref={retailerCodeRef}
                  type="text"
                  onInput={handleRetailerCodeInput}
                  placeholder="e.g., 2302679 or 2302679(R)"
                  maxLength="10"
                  disabled={loading}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="username"
                  spellCheck="false"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-left block">
                    Password
                  </Label>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-xs font-normal"
                    onClick={onResetPassword}
                    type="button"
                    tabIndex={-1}
                  >
                    Reset Password
                  </Button>
                </div>
                <Input
                  id="password"
                  ref={passwordRef}
                  type="password"
                  placeholder="Enter your password"
                  disabled={loading}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="current-password"
                  spellCheck="false"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full mt-6" 
              disabled={loading}
              aria-label={loading ? 'Signing in, please wait' : 'Sign in to your account'}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground text-center">
            New user?{' '}
            <Button 
              variant="link" 
              className="p-0 h-auto font-semibold" 
              onClick={onSignup}
            >
              Sign up
            </Button>
          </p>
          
          {/* Policy Links */}
          <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground border-t pt-4">
            <button
              onClick={() => handlePolicyClick('privacy')}
              className="hover:text-primary hover:underline transition-colors"
            >
              Privacy Policy
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              onClick={() => handlePolicyClick('terms')}
              className="hover:text-primary hover:underline transition-colors"
            >
              Terms of Service
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              onClick={() => handlePolicyClick('refund')}
              className="hover:text-primary hover:underline transition-colors"
            >
              Refund Policy
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              onClick={() => handlePolicyClick('cancellation')}
              className="hover:text-primary hover:underline transition-colors"
            >
              Cancellation Policy
            </button>
          </div>
        </CardFooter>
      </Card>

      {/* Policy Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedPolicy && policies[selectedPolicy]?.title}
            </DialogTitle>
            <DialogDescription>
              Please read the following terms and conditions carefully.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {selectedPolicy && policies[selectedPolicy]?.content()}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Login;